import {
  ValidateNested,
  IsDefined,
  IsString,
  IsArray,
  IsEnum,
  IsOptional,
  IsIn,
  IsBoolean,
  ValidateIf,
  Length,
  IsEmail,
  ArrayMinSize,
  IsISO8601,
  IsInt,
  Max,
  Min,
  MaxLength,
  MinLength,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsCuid } from 'lib/validation/IsCuid';
import {
  DeleteEndpoint,
  GetEndpoint,
  PostEndpoint,
  PutEndpoint,
} from 'types/Endpoint';

export enum FlatEmailDeliveryStatus {
  SENT = 'sent',
  REJECTED = 'rejected',
  BOUNCED = 'bounced',
  DELIVERED = 'delivered',
  DELAYED = 'delayed',
}

export enum CommunicationResponseStatus {
  pending = 'pending',
  responded = 'responded',
  expired = 'expired',
}

export type EngagePropertiesSortDirection = 'asc' | 'desc';
export type EngagePropertiesSortField = 'contacts' | 'flats';

enum FlatOccupationStatus {
  OCCUPIED = 'occupied',
  UNOCCUPIED = 'unoccupied',
  UNKNOWN = 'unknown',
}

export enum FlatIssueType {
  RESIDENT_ISSUES = 'resident-issues',
  OWNER_ISSUES = 'owner-issues',
  EMAIL_ISSUES = 'email-issues',
  CONTACT_SURVEY_RESPONSE_OVERDUE = 'contact-survey-response-overdue',
  CONTACT_SURVEY_REQUIRED = 'contact-survey-required',
}

export enum FlatRequiresResidentSupport {
  REQUIRED = 'required',
  NOT_REQUIRED = 'not-required',
}

export enum EngageFlatIsHMO {
  UNKNOWN = 'UNKNOWN',
  NO = 'NO',
  YES = 'YES',
}

export enum EngageFlatIsCommerciallyOwned {
  UNKNOWN = 'UNKNOWN',
  NO = 'NO',
  YES = 'YES',
}

export enum EngageScheduledVisitStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  CANCELLED = 'cancelled',
}

enum FlatContactType {
  resident = 'resident',
  residentOwner = 'resident-owner',
  owner = 'owner',
  movedSold = 'moved-sold',
  noAssociation = 'no-association',
  other = 'other',
  lettingAgent = 'letting-agent',
  onBehalfOf = 'on-behalf-of',
}

enum EngageContactArchivedReason {
  notMe = 'not-me',
  movedOut = 'moved-out',
  stoppedOwning = 'stopped-owning',
  movedOutStoppedOwning = 'moved-out-stopped-owning',
  mistake = 'mistake',
  other = 'other',
}

enum DataSource {
  'engage' = 'engage',
  'app' = 'app',
}

type ISODateString = string & { __brand: 'ISODate' };

enum CommunicationStatus {
  requested = 'requested',
  sent = 'sent',
  failed = 'failed',
}

enum CommunicationType {
  email = 'email',
  letter = 'letter',
}

enum CommunicationAccessedFrom {
  'letterQr' = 'letter-qr',
  'emailLink' = 'email-link',
  'app' = 'app',
  'shortCode' = 'short-code',
  'unknown' = 'unknown',
}

export enum ScheduledVisitCommunicationScheduleTemplateType {
  REMINDER = 'reminder',
  CHASER = 'chaser',
  REQUEST = 'request',
  CANCELLATION = 'cancellation',
}

export enum EngageContactInteractionType {
  flatContactViewEvacuationInfo = 'flat-contact-view-evacuation-info',
  flatContactEditEvacuationInfo = 'flat-contact-edit-evacuation-info',
}

export type EngageContactInteractionLog = {
  interactionType: EngageContactInteractionType;
  name: string;
  email: string;
  createdDate: ISODateString;
};

export type EngageContactEvacuationInfo = {
  hasSupportNeeds: boolean;
  hasMobilityLimitation: boolean;
  hasCognitiveImpairment: boolean;
  hasSocialAnxiety: boolean;
  hasHearingImpairment: boolean;
  emergencyEvacuationStatement: string;
  emergencyEvacuationStatementAgreedDate: ISODateString | null;
  emergencyEvacuationSharingAgreedDate: ISODateString | null;
  emergencyEvacuationAssistanceSummary: string;
};

export type EngageCommunication = {
  id: string;
  createdDate: ISODateString;
  name: string;
  description: string;
  type: CommunicationType;
  contactId: string | null;
  address: string;
  status: CommunicationStatus;
  statusDate: ISODateString;

  link: string;
  linkAccessed: {
    from: CommunicationAccessedFrom;
    date: ISODateString;
  } | null;
  responseStatus: CommunicationResponseStatus;
  statusFlags: CommunicationStatusFlags;
};

export type CommunicationStatusFlags = {
  sent: boolean;
  sentDate: ISODateString | null;
  bounced: boolean;
  bouncedDate: ISODateString | null;
  rejected: boolean;
  rejectedDate: ISODateString | null;
  delayed: boolean;
  delayedDate: ISODateString | null;
  opened: boolean;
  openedDate: ISODateString | null;
  responded: boolean;
  respondedDate: ISODateString | null;
  due: boolean;
  dueDate: ISODateString | null;
  expired: boolean;
  expiredDate: ISODateString | null;
  overDue: boolean;
  overDueDate: ISODateString | null;
};

export type EngageContact = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isOwner: boolean;
  isResident: boolean;
  isUnder16: boolean;
  hasSupportNeeds: boolean;
  comments: string;
  internalNotes: string;
  contactType: FlatContactType;
  addedTimestamp: ISODateString;
  addedVia: DataSource;
  archivedTimestamp: ISODateString | null;
  archivedVia: DataSource | null;
  archivedReason: EngageContactArchivedReason | null;
  confirmedDate: ISODateString | null;
  confirmationExpiryDate: ISODateString | null;
  interactionLogs: EngageContactInteractionLog[];
};

/**
 * Get Properties
 */

export enum BlockManagerPropertyFeatureEnums {
  'scheduledvisits' = 'scheduledvisits',
  'importflatcontacts' = 'importflatcontacts',
}

export type BlockManagerGetPropertiesRequest = GetEndpoint<{
  url: '/blockmanagement/properties';
  query: {
    page: number;
    search?: string;
    sortBy?: EngagePropertiesSortField;
    sortDirection?: EngagePropertiesSortDirection;
  };
  response: {
    totalCount: number;
    properties: {
      id: string;
      reference: string;
      customReference: string;
      name: string;
      parentName: string;
      addressLines: string[];
      postcode: string;
      organisationId: string;
      engageUrl: string;
      noOfFlats: number;
      noOfContacts: number;
      features: BlockManagerPropertyFeatureEnums[];
    }[];
    expandedData: {
      organisations: Record<string, { name: string }>;
    };
  };
}>;

/**
 * Onboard Flats
 */

export class BlockManagerOnboardFlatsParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;
}

export type BlockManagerOnboardFlatsRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/onboard';
  params: typeof BlockManagerOnboardFlatsParams;
  query: {};
  body: {};
  response: {
    onboarded: number;
    updated: number;
    archived: number;
  };
}>;

/**
 * Post Schedule Check
 */

export class BlockManagerPostScheduledVisitParams {
  @IsDefined()
  @IsString()
  propertyUid!: string;
}

export class BlockManagerScheduledVisitTimeslot {
  constructor(data: BlockManagerScheduledVisitTimeslot) {
    Object.assign(this, data);
  }

  @IsDefined()
  @IsISO8601()
  date!: ISODateString;

  @IsDefined()
  @IsArray()
  @ArrayMinSize(1)
  @IsInt({ each: true })
  @Min(0, { each: true })
  @Max(23, { each: true })
  hours!: number[];
}

export class BlockManagerPostScheduledVisitBody {
  constructor(data: BlockManagerPostScheduledVisitBody) {
    Object.assign(this, data);
  }

  @IsDefined()
  @IsEnum(EngageScheduledVisitStatus)
  status!: EngageScheduledVisitStatus;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => BlockManagerScheduledVisitTimeslot)
  @IsDefined()
  @ArrayMinSize(1)
  timeslots!: BlockManagerScheduledVisitTimeslot[];
}

export type BlockManagerPostScheduledVisitRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/scheduled-visit';
  params: typeof BlockManagerPostScheduledVisitParams;
  query: {};
  body: typeof BlockManagerPostScheduledVisitBody;
  response: { scheduledVisitId: string };
}>;

export class BlockManagerUpdateScheduledVisitParams {
  @IsDefined()
  @IsString()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsString()
  @IsCuid()
  scheduledVisitId!: string;
}

export class BlockManagerUpdateScheduledVisitBody {
  constructor(data: BlockManagerUpdateScheduledVisitBody) {
    Object.assign(this, data);
  }

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => BlockManagerScheduledVisitTimeslot)
  @IsDefined()
  @ArrayMinSize(1)
  timeslots!: BlockManagerScheduledVisitTimeslot[];

  @IsDefined()
  @IsEnum(EngageScheduledVisitStatus)
  status!: EngageScheduledVisitStatus;
}

export type BlockManagerUpdateScheduledVisitRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId';
  params: typeof BlockManagerUpdateScheduledVisitParams;
  query: {};
  body: typeof BlockManagerUpdateScheduledVisitBody;
  response: {};
}>;

export class BlockManagerCancelScheduledVisitParams {
  @IsDefined()
  @IsString()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsString()
  @IsCuid()
  scheduledVisitId!: string;
}

export class BlockManagerCancelScheduledVisitBody {
  constructor(data: BlockManagerCancelScheduledVisitBody) {
    Object.assign(this, data);
  }

  @IsDefined()
  @IsString()
  @Length(1, 1000)
  cancelledReason!: string;
}

export type BlockManagerCancelScheduledVisitRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/cancel';
  params: typeof BlockManagerCancelScheduledVisitParams;
  query: {};
  body: typeof BlockManagerCancelScheduledVisitBody;
  response: {};
}>;

/**
 * Get Flats
 */

export type BlockManagerGetFlatsRequestFlat = {
  id: string;
  name: string;
  reference: string;
  customReference: string;
  surveyResponseDate: ISODateString | null;
  occupationStatus: FlatOccupationStatus;
  isHMO: EngageFlatIsHMO;
  isCommerciallyOwned: EngageFlatIsCommerciallyOwned;
  issues: {
    hasNoResidents: boolean;
    hasNoOwners: boolean;
    isMissingResidents: boolean;
    hasExpiredResidentConfirmations: boolean;
    hasExpiredOwnerConfirmations: boolean;
    hasOwnerIssues: boolean;
    hasResidentIssues: boolean;
    hasEmailIssues: boolean;
    requiresContactSurvey: boolean;
    hasSurveyResponseOverdue: boolean;
  };
  confirmations: {
    oldestResidentConfirmationDate: ISODateString | null;
    oldestOwnerConfirmationDate: ISODateString | null;
    residentConfirmationExpiryDate: ISODateString | null;
    ownerConfirmationExpiryDate: ISODateString | null;
  };
  matchingContactIds: string[];
  contacts: {
    id: string;
    name: string;
    email: string;
    phone: string;
    isOwner: boolean;
    isResident: boolean;
    isPrimary: boolean;
    isUnder16: boolean;
    hasSupportNeeds: boolean;
    contactType: FlatContactType;
    confirmedDate: ISODateString | null;
    confirmationExpiryDate: ISODateString | null;
  }[];
};

export type BlockManagerGetFlatsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats';
  query: {
    page: number;
    search?: string;
    occupationStatuses?: FlatOccupationStatus[];
    issueContactTypes?: FlatIssueType[];
    requiresResidentSupport?: FlatRequiresResidentSupport[];
    isHMO?: EngageFlatIsHMO[];
    isCommerciallyOwned?: EngageFlatIsCommerciallyOwned[];
    emailDeliveryStatuses?: FlatEmailDeliveryStatus[];
  };
  response: {
    totalCount: number;
    flats: BlockManagerGetFlatsRequestFlat[];
  };
}>;

/**
 * Get Flat
 */

export type BlockManagerGetFlatRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid';
  query: {};
  response: {
    contacts: EngageContact[];
    communications: EngageCommunication[];
  };
}>;

/**
 * Get Flat Communications
 */
export type BlockManagerGetFlatCommunicationsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/communications';
  query: {};
  response: {
    communications: EngageCommunication[];
  };
}>;

/**
 * Get Message Preview URL
 */
export class BlockManagerGetMessagePreviewUrlParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsCuid()
  flatUid!: string;

  @IsDefined()
  @IsCuid()
  communicationUid!: string;
}

export type BlockManagerGetMessagePreviewUrlRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/communications/:communicationUid/previewurl';
  params: typeof BlockManagerGetMessagePreviewUrlParams;
  query: {};
  response: { url: string };
}>;

/**
 * Get Download Contacts CSV Template
 */
export type BlockManagerGetDownloadContactsCSVTemplateRequest = GetEndpoint<{
  url: '/blockmanagement/properties/contacts-import-template';
  query: {};
  response: {
    url: string;
    fileName: string;
  };
}>;

/**
 * Send Communication
 */
export class BlockManagerSendCommsParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsCuid()
  flatUid!: string;
}

export class BlockManagerSendCommsBody {
  @IsDefined()
  @IsString()
  communicationTemplateId!: string;

  // @MinLength(1, { each: true }) // this can be an empty array for letters
  @IsDefined()
  @IsArray()
  @IsCuid({ each: true }) // if present, then must be an array of cuids
  contactIds!: string[];
}

export type BlockManagerSendCommunicationRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/sendcommunication';
  body: typeof BlockManagerSendCommsBody;
  params: typeof BlockManagerSendCommsParams;
  query: {};
  response: {
    communications: EngageCommunication[];
  };
}>;

/**
 * Bulk Send Communication
 */
export class BlockManagerSendCommunicationBulkParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;
}

export class BlockManagerSendCommunicationBulkBody {
  @IsDefined()
  @IsString()
  communicationTemplateId!: string;

  @IsDefined()
  @IsArray()
  @ArrayMinSize(1)
  @IsCuid({ each: true })
  flatIds!: string[];

  @IsDefined()
  @IsBoolean()
  residents!: boolean;

  @IsDefined()
  @IsBoolean()
  owners!: boolean;

  @IsOptional()
  @IsBoolean()
  primaryContactsOnly?: boolean;
}

export type BlockManagerSendCommunicationBulkRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/sendcommunicationbulk';
  params: typeof BlockManagerSendCommunicationBulkParams;
  body: typeof BlockManagerSendCommunicationBulkBody;
  query: {};
  response: {};
}>;

/**
 * Shared params
 */

export class BlockManagerFlatParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsCuid()
  flatUid!: string;
}

export class BlockManagerPropertyParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;
}

/**
 * Add Contact
 */

export class BlockManagerAddContactParams extends BlockManagerFlatParams {}

// Return true if contact is 16 or over
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flatContactIs16OrOver = (data: any): boolean =>
  !data.isUnder16 || !!data.isOwner || !data.isResident;

export class NewFlatContactDTO {
  constructor(data: NewFlatContactDTO) {
    Object.assign(this, data);
  }

  @IsDefined({ message: 'id is required.' })
  @IsString()
  id!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o))
  @IsDefined({ message: 'firstName is required.' })
  @IsString()
  @Length(1, 50)
  firstName!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o))
  @IsDefined({ message: 'lastName is required.' })
  @IsString()
  @Length(1, 50)
  lastName!: string;

  @ValidateIf(
    (o) =>
      typeof o.email !== 'string' ||
      (flatContactIs16OrOver(o) && o.email !== ''),
  )
  @IsString()
  @IsEmail()
  email!: string;

  @ValidateIf(
    (o) =>
      typeof o.phone !== 'string' ||
      (flatContactIs16OrOver(o) && o.phone !== ''),
  )
  @IsString()
  @Length(3, 20)
  phone!: string;

  @IsDefined({ message: 'isResident is required.' })
  @IsBoolean()
  isResident!: boolean;

  @IsDefined({ message: 'isOwner is required.' })
  @IsBoolean()
  isOwner!: boolean;

  // Validate contactType if contact is not an owner or resident
  @ValidateIf((o) => !o.isOwner && !o.isResident)
  @IsEnum(FlatContactType)
  contactType!: FlatContactType;

  @IsString()
  @Length(0, 1000)
  internalNotes!: string;

  @IsString()
  @Length(0, 1000)
  comments!: string;

  @IsDefined({ message: 'isUnder16 is required.' })
  @IsBoolean()
  isUnder16!: boolean;

  @IsOptional()
  @IsBoolean()
  isFormFiller?: boolean;

  @IsOptional()
  @IsBoolean()
  isPrimary?: boolean;

  @IsOptional()
  @IsBoolean()
  isContactable?: boolean;
}

export class BlockManagerAddContactBody {
  constructor(data: BlockManagerAddContactBody) {
    Object.assign(this, data);
  }

  @ValidateNested()
  @Type(() => NewFlatContactDTO)
  @IsDefined()
  contact!: NewFlatContactDTO;
}

export type BlockManagerAddContactRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contact';
  params: typeof BlockManagerAddContactParams;
  query: {};
  body: typeof BlockManagerAddContactBody;
  response: {
    message: string;
    flatOccupationStatus: FlatOccupationStatus;
    issues: BlockManagerGetFlatsRequestFlat['issues'];
    contact: EngageContact;
  };
}>;

/**
 * Update Contact
 */

export class BlockManagerContactParams extends BlockManagerFlatParams {
  @IsDefined({ message: 'contactUid is required.' })
  @IsCuid()
  contactUid!: string;
}

export class BlockManagerUpdateContactParams extends BlockManagerContactParams {}

export class BlockManagerUpdateContactBody {
  constructor(data: BlockManagerUpdateContactBody) {
    Object.assign(this, data);
  }

  @ValidateNested()
  @Type(() => NewFlatContactDTO)
  @IsDefined({ message: 'contact is required.' })
  contact!: NewFlatContactDTO;
}

export type BlockManagerUpdateContactRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid';
  params: typeof BlockManagerUpdateContactParams;
  query: {};
  body: typeof BlockManagerUpdateContactBody;
  response: {
    flatOccupationStatus: FlatOccupationStatus;
    issues: BlockManagerGetFlatsRequestFlat['issues'];
    contact: EngageContact;
  };
}>;

/**
 * Archive / Unarchive Contact
 */

export class BlockManagerArchiveContactParams extends BlockManagerContactParams {
  @IsDefined()
  @IsIn(['archive', 'unarchive'])
  action!: 'archive' | 'unarchive';
}

export class BlockManagerArchiveContactBody {
  constructor(data: BlockManagerArchiveContactBody) {
    Object.assign(this, data);
  }

  @IsOptional()
  @IsEnum(EngageContactArchivedReason)
  archivedReason?: EngageContactArchivedReason;
}

export type BlockManagerArchiveContactRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/:action';
  params: typeof BlockManagerArchiveContactParams;
  query: {};
  body: typeof BlockManagerArchiveContactBody;
  response: {
    contact: EngageContact | null;
    flatOccupationStatus: FlatOccupationStatus;
    issues: BlockManagerGetFlatsRequestFlat['issues'];
  };
}>;

/**
 * Get Contact
 */

export class BlockManagerGetContactParams extends BlockManagerContactParams {}

export type BlockManagerGetContactRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid';
  params: typeof BlockManagerGetContactParams;
  query: {};
  response: {
    contact: EngageContact;
  };
}>;

export type BlockManagerGetFlatContactEvacuationInfoRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/evacuation-info';
  params: typeof BlockManagerGetContactParams;
  query: {};
  response: EngageContactEvacuationInfo;
}>;

export class BlockManagerListContactsQuery {
  @IsOptional()
  @IsIn(['active', 'archived', 'both'])
  state?: 'active' | 'archived' | 'both';
}

export type BlockManagerListContactsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts';
  params: typeof BlockManagerFlatParams;
  query: BlockManagerListContactsQuery;
  response: {
    contacts: EngageContact[];
  };
}>;

/**
 * Create Flat
 */

export class BlockManagerLinkFlatElementParams {
  @IsCuid()
  @IsDefined()
  element_uid!: string;
}

type BlockManagerLinkFlatElementResponse = {
  message: string;
  flatElement: {
    propertyId: number; // ID of property in RiskBase
    assetId: number; // ID of flat in RiskBase
    groupId: number; // ID of field group that assetId is in
    organisationId: number;
    flatId: number | null;
    flatUid: string | null;
  };
  linkId?: number;
  created: boolean;
};

export type BlockManagerLinkFlatElementRequest = PutEndpoint<{
  url: '/blockmanagement/create-flat-element/:element_uid';
  params: typeof BlockManagerLinkFlatElementParams;
  body: {};
  query: {};
  response: BlockManagerLinkFlatElementResponse;
}>;

/**
 * Update Flat
 */

export class BlockManagerUpdateFlatParams {
  @IsCuid()
  @IsDefined({ message: 'propertyUid is required.' })
  propertyUid!: string;

  @IsCuid()
  @IsDefined({ message: 'flatUid is required.' })
  flatUid!: string;
}

export class BlockManagerUpdateFlatBody {
  constructor(data: BlockManagerUpdateFlatBody) {
    Object.assign(this, data);
  }

  @IsOptional()
  @IsEnum(FlatOccupationStatus)
  occupationStatus?: FlatOccupationStatus;

  @IsOptional()
  @IsEnum(EngageFlatIsHMO)
  isHMO?: EngageFlatIsHMO;

  @IsOptional()
  @IsEnum(EngageFlatIsCommerciallyOwned)
  isCommerciallyOwned?: EngageFlatIsCommerciallyOwned;
}

type BlockManagerUpdateFlatResponse = {
  updatedContacts: EngageContact[];
  archivedContactIds: string[];
  isHMO: EngageFlatIsHMO;
  isCommerciallyOwned: EngageFlatIsCommerciallyOwned;
  issues: BlockManagerGetFlatsRequestFlat['issues'];
};

export type BlockManagerUpdateFlatRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid';
  params: typeof BlockManagerUpdateFlatParams;
  body: typeof BlockManagerUpdateFlatBody;
  query: {};
  response: BlockManagerUpdateFlatResponse;
}>;

/**
 * Upload Contact Details
 */
export class BlockManagerUploadFlatContactsParams {
  @IsCuid()
  @IsDefined({ message: 'propertyUid is required.' })
  propertyUid!: string;
}

export class BlockManagerUploadFlatContactsBody {
  constructor(data: BlockManagerUploadFlatContactsBody) {
    Object.assign(this, data);
  }
}

export type BlockManagerUploadFlatContactsRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/contacts/upload';
  params: typeof BlockManagerUploadFlatContactsParams;
  query: {};
  body: typeof BlockManagerUploadFlatContactsBody;
  response: {
    success: boolean;
    message: string;
  };
}>;

export class BlockManagerUpdateContactEvacuationInfoBody {
  constructor(data: BlockManagerUpdateContactEvacuationInfoBody) {
    Object.assign(this, data);
  }

  @IsDefined()
  @IsBoolean()
  hasSupportNeeds!: boolean;

  @ValidateIf((o) => o.hasSupportNeeds)
  @IsDefined()
  @IsBoolean()
  hasMobilityLimitation!: boolean;

  @ValidateIf((o) => o.hasSupportNeeds)
  @IsDefined()
  @IsBoolean()
  hasCognitiveImpairment!: boolean;

  @ValidateIf((o) => o.hasSupportNeeds)
  @IsDefined()
  @IsBoolean()
  hasSocialAnxiety!: boolean;

  @ValidateIf((o) => o.hasSupportNeeds)
  @IsDefined()
  @IsBoolean()
  hasHearingImpairment!: boolean;

  @ValidateIf(
    (o) =>
      o.hasSupportNeeds && o.emergencyEvacuationStatementAgreedDate !== null,
  )
  @IsDefined()
  @IsISO8601()
  emergencyEvacuationStatementAgreedDate!: ISODateString | null;

  @ValidateIf(
    (o) => o.hasSupportNeeds && o.emergencyEvacuationSharingAgreedDate !== null,
  )
  @IsDefined()
  @IsISO8601()
  emergencyEvacuationSharingAgreedDate!: ISODateString | null;

  @ValidateIf((o) => o.hasSupportNeeds)
  @IsDefined()
  @IsString()
  @MaxLength(1000)
  @MinLength(1, {
    validateIf: (o) => o.emergencyEvacuationStatementAgreedDate !== null,
  })
  emergencyEvacuationStatement!: string;

  @ValidateIf((o) => o.hasSupportNeeds)
  @IsDefined()
  @IsString()
  @MaxLength(300)
  @MinLength(1, {
    validateIf: (o) => o.emergencyEvacuationSharingAgreedDate !== null,
  })
  emergencyEvacuationAssistanceSummary!: string;
}

export type BlockManagerPutFlatContactEvacuationInfoRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/evacuation-info';
  params: typeof BlockManagerContactParams;
  query: {};
  body: typeof BlockManagerUpdateContactEvacuationInfoBody;
  response: {};
}>;

/**
 * Download Flats
 */
export type BlockManagerDownloadFlatsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/export-flats';
  params: {};
  query: {};
  response: {
    url: string;
    fileName: string;
  };
}>;

/**
 * Get Scheduled Visits
 */

export type ScheduledVisitInspector = {
  id: string;
  forename: string;
  surname: string;
  email: string;
  avatarUrl: string | null;
};

export class BlockManagerGetScheduledVisitsQuery {
  @IsOptional()
  @IsIn(['true', 'false'])
  include_historic?: 'true' | 'false';
}

export type BlockManagerGetScheduledVisitsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/scheduled-visits';
  query: BlockManagerGetScheduledVisitsQuery;
  response: {
    scheduledVisits: {
      publishedDate: ISODateString | null;
      cancelledDate: ISODateString | null;
      cancelledReason: string | null;
      startDateTime: ISODateString | null;
      endDateTime: ISODateString | null;
      status: EngageScheduledVisitStatus;
      historic: boolean;
      id: string;
      slots: BlockManagerScheduledVisitTimeslot[];
      inspectors: ScheduledVisitInspector[];
    }[];
  };
}>;

/**
 * Scheduled Visit Recipients
 */

export enum EngageScheduledVisitRecipientMessageDeliverabilityStatus {
  ERROR = 'error',
  WARNING = 'warning',
  OKAY = 'okay',
}

export type EngageScheduledVisitRecipientMessageDeliverabilityStatusReason =
  | 'no-contacts'
  | 'no-residents'
  | 'no-resident-emails'
  | 'no-contact-emails'
  | 'no-primary-residents'
  | 'primary-residents';

export type EngageScheduledVisitRecipient = {
  id: string;
  name: string;
  deliverabilityStatus: EngageScheduledVisitRecipientMessageDeliverabilityStatus;
  deliverabilityStatusReason: EngageScheduledVisitRecipientMessageDeliverabilityStatusReason;
};

export type EngageScheduledVisitRecipients = {
  flats: EngageScheduledVisitRecipient[];
  summary: {
    emails: number;
    letters: number;
  };
};

export type BlockManagerGetScheduledVisitRecipientsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/scheduled-visit-recipients';
  params: typeof BlockManagerPropertyParams;
  query: {};
  response: EngageScheduledVisitRecipients;
}>;
/**
 * Scheduled Visit Communication Templates
 */
export type ScheduledVisitMessageTemplateName =
  | 'Request'
  | 'Reminder'
  | 'Chaser'
  | 'Cancellation';

export type ScheduledVisitMessageTemplateId =
  | 'door_check_scheduled_visit_request_email'
  | 'door_check_scheduled_visit_reminder_email'
  | 'door_check_scheduled_visit_chaser_email'
  | 'door_check_scheduled_visit_cancellation_email';

export type ScheduledVisitMessageTemplate = {
  id: ScheduledVisitMessageTemplateId;
  name: ScheduledVisitMessageTemplateName;
  description: string;
  subject: string;
  body: string;
  sendDays: number;
};

export type ScheduledVisitCommunicationSchedule = {
  templateType: ScheduledVisitCommunicationScheduleTemplateType;
  sentDate: ISODateString | null;
  failedDate: ISODateString | null;
};

export type BlockManagerGetScheduledVisitTemplatesRequest = GetEndpoint<{
  url: '/blockmanagement/scheduled-visit-templates';
  query: {};
  response: {
    messages: ScheduledVisitMessageTemplate[];
  };
}>;

export class BlockManagerGetScheduledVisitCommunicationScheduleParams {
  @IsCuid()
  @IsDefined({ message: 'propertyUid is required.' })
  propertyUid!: string;

  @IsCuid()
  @IsDefined({ message: 'scheduledVisitId is required.' })
  scheduledVisitId!: string;
}

export type BlockManagerGetScheduledVisitCommunicationScheduleRequest =
  GetEndpoint<{
    url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/communication-schedule';
    params: typeof BlockManagerGetScheduledVisitCommunicationScheduleParams;
    query: {};
    response: {
      scheduledVisitCommunicationSchedule: ScheduledVisitCommunicationSchedule[];
    };
  }>;

/**
 * Update Inspectors
 */
export class BlockManagerUpdateInspectorsParams {
  @IsCuid()
  @IsDefined({ message: 'propertyUid is required.' })
  propertyUid!: string;

  @IsCuid()
  @IsDefined({ message: 'scheduledVisitId is required.' })
  scheduledVisitId!: string;
}

export class BlockManagerUpdateInspectorsBody {
  constructor(data: BlockManagerUpdateInspectorsBody) {
    Object.assign(this, data);
  }

  @IsDefined()
  @IsArray()
  @IsCuid({ each: true })
  inspectorIds!: string[];
}

export type BlockManagerUpdateInspectorsRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/inspectors';
  params: typeof BlockManagerUpdateInspectorsParams;
  query: {};
  body: typeof BlockManagerUpdateInspectorsBody;
  response: {};
}>;

/**
 * Get Inspectors
 */

export class BlockManagerGetPropertyAccountsParams extends BlockManagerPropertyParams {}

export type PropertyAccount = {
  id: string;
  forename: string;
  surname: string;
  email: string;
  avatarUrl: string | null;
};

export type BlockManagerGetPropertyAccountsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/accounts';
  params: typeof BlockManagerGetPropertyAccountsParams;
  query: {};
  response: {
    accounts: PropertyAccount[];
  };
}>;

/**
 * Delete Scheduled Visits
 */
export class BlockManagerDeleteScheduledVisitParams {
  @IsCuid()
  @IsDefined({ message: 'propertyUid is required.' })
  propertyUid!: string;

  @IsCuid()
  @IsDefined({ message: 'scheduledVisitId is required.' })
  scheduledVisitId!: string;
}

export type BlockManagerDeleteScheduledVisitRequest = DeleteEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId';
  params: typeof BlockManagerDeleteScheduledVisitParams;
  query: {};
  response: {};
}>;
